import { API } from '@/modules/api'
import type { TDestinyFineCartLine } from '@/types/cart'
import { defineStore } from 'pinia'
import { OrderItemType, OrderItemTypeCode } from '@/types/orderItemType'
import type { DestinyFine } from '@/modules/api/get/destinyFines.ts'
import { useCartStore } from '@/stores/cart'

export const createUniqueId = (feeId: number) => feeId + '::' + OrderItemTypeCode.DestinyFine

export const useDestinyFineStore = defineStore('destinyFine', {
  state: () => ({
    isLoaded: {} as Record<TStudentAccountId, boolean>,
    fines: {} as Record<TStudentAccountId, DestinyFine[]>
  }),

  getters: {
    isLoading(state) {
      return (studentAccountId: TStudentAccountId) => !(state.isLoaded[studentAccountId] ?? false)
    },

    getFines(state) {
      return (studentAccountId: TStudentAccountId) => state.fines[studentAccountId] ?? []
    }
  },

  actions: {
    async fetch(_districtId: TDistrictId, studentAccountId: TStudentAccountId) {
      this.isLoaded[studentAccountId] = false
      this.fines[studentAccountId] = await API.Get.destinyFines(studentAccountId)
      this.validateCart(_districtId, studentAccountId)
      this.isLoaded[studentAccountId] = true
    },

    // This function is used to validate the cart lines for the destiny fine store
    // It checks if the cart lines are valid compared to the data from the API, and if not, it removes them from the cart.
    // This prevents the cart from attempting to get order line estimates or checking out with invalid data.
    validateCart(districtId: TDistrictId, studentAccountId: TStudentAccountId) {
      const cartStore = useCartStore()
      for (const line of cartStore.getCartLines(districtId) as TDestinyFineCartLine[]) {
        if (
          line.orderItemType === OrderItemType.DestinyFine &&
          line.studentAccountId === studentAccountId
        ) {
          const fine = this.fines[studentAccountId].find(fine => fine.feeId === line.feeId)
          if (!fine) {
            console.log('fine not found, removing from cart', line.feeId)
            this.removeFromCart(districtId, line.feeId)
          }
          // ensure fee amount is less than cart amountn meaning cart amount is too high
          if (fine && fine.amountDue < line.amount) {
            console.log('fine amount is less than cart amount, adjusting cart', line.feeId)
            cartStore.updateCartLine(districtId, line.uniqueId, {
              ...line,
              amount: fine.amountDue
            })
          }
        }
      }
    },

    addToCart(
      districtId: TDistrictId,
      feeData: Omit<TDestinyFineCartLine, 'orderItemType' | 'uniqueId'>
    ) {
      const cartStore = useCartStore()
      const uniqueId = createUniqueId(feeData.feeId)
      const existingLine = cartStore.getCartLine(districtId, uniqueId)

      if (existingLine) {
        cartStore.updateCartLine(districtId, uniqueId, feeData)
      } else {
        cartStore.addCartLine(districtId, uniqueId, {
          ...feeData,
          orderItemType: OrderItemType.DestinyFine,
          uniqueId
        })
      }
    },

    updateCartLine(
      districtId: TDistrictId,
      feeData: Partial<TDestinyFineCartLine> & { feeId: number }
    ) {
      const cartStore = useCartStore()
      const uniqueId = createUniqueId(feeData.feeId)
      cartStore.updateCartLine(districtId, uniqueId, feeData)
    },

    removeFromCart(districtId: TDistrictId, feeId: number) {
      const cartStore = useCartStore()
      const uniqueId = createUniqueId(feeId)
      cartStore.removeCartLine(districtId, uniqueId)
    }
  }
})
